import React, { useEffect, useState, useContext, useCallback } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import UserContext from "../context/UserContext";
import StripePay from "./StripePay";
import { api } from "../utils/apiHelper";
import "../styles/CheckOut.css";

const stripePromise = loadStripe("pk_test_gHRNbIXqJnr5ead25WqdZ4uX");

const CheckOut = () => {
  const { authUser } = useContext(UserContext);
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [showGuestCard, setShowGuestCard] = useState(false);
  const [guestEmail, setGuestEmail] = useState("");
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [purchaseSuccess, setPurchaseSuccess] = useState(false);
  const [purchaseMessage, setPurchaseMessage] = useState("");

  const getCartUserId = () => {
    if (authUser?._id) return authUser._id;

    let guestId = localStorage.getItem("guestUserId");

    if (!guestId) {
      guestId = `guest-${crypto.randomUUID()}`;
      localStorage.setItem("guestUserId", guestId);
    }

    return guestId;
  };

  const [cartUserId, setCartUserId] = useState(getCartUserId);

  useEffect(() => {
    setCartUserId(getCartUserId());
  }, [authUser]);

  const fetchCart = useCallback(async () => {
    if (!cartUserId) {
      setLoading(false);
      return;
    }

    try {
      const data = await api(`/cart/${cartUserId}`);

      if (Array.isArray(data)) {
        setCartItems(data);

        const calcTotal = data.reduce(
          (sum, item) => sum + parseFloat(item.price || 0),
          0
        );

        setTotal(calcTotal);
      } else {
        console.error("Unexpected cart response format:", data);
      }
    } catch (error) {
      console.error("Error fetching cart:", error);
    } finally {
      setLoading(false);
    }
  }, [cartUserId]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const handleGuestCheckout = (e) => {
    e.preventDefault();

    if (!guestEmail) {
      alert("Please enter an email.");
      return;
    }

    const guestId = getCartUserId();

    localStorage.setItem("guestUserId", guestId);
    localStorage.setItem("guestEmail", guestEmail);

    setCartUserId(guestId);
    setShowGuestCard(false);
    setShowPaymentForm(true);
  };

  const handlePaymentSuccess = async (message) => {
    setShowPaymentForm(false);
    setPurchaseSuccess(true);
    setPurchaseMessage(message || "");
    await fetchCart();
  };

  const handleProceedToPayment = () => {
    const userId = getCartUserId();
    const isLoggedIn = authUser?._id;
    const hasGuestEmail = localStorage.getItem("guestEmail");

    setCartUserId(userId);

    if (!isLoggedIn && !hasGuestEmail) {
      setShowGuestCard(true);
      setShowPaymentForm(false);
      return;
    }

    setShowGuestCard(false);
    setShowPaymentForm(true);
  };

  if (loading) return <p>Loading your cart...</p>;

  if (!cartItems.length && !purchaseSuccess) {
    return <p className="myCart">Your cart is empty.</p>;
  }

  return (
    <div className="check-container">
      <h2 className="myCart">Your Cart</h2>

      {purchaseSuccess && (
        <div className="success-message">
          ✅ {purchaseMessage || "Thank you for your purchase! Your items are now available."}
        </div>
      )}

      {!purchaseSuccess && (
        <>
          <ul className="files-list-order">
            {cartItems.map((item, index) => (
              <li key={`${item.projectId}-${index}`} className="product-card">
                <h3>{item.projectTitle}</h3>
                <p><strong>Seller:</strong> {item.ownerUsername || item.ownerId}</p>
                <p><strong>Category:</strong> {item.productCat}</p>
                <p><strong>Price:</strong> ${item.price}</p>
                {item.quantityAvailable !== null && (
                  <p><strong>Quantity Available:</strong> {item.quantityAvailable}</p>
                )}
                <p><strong>Added:</strong> {new Date(item.addedAt).toLocaleString()}</p>
              </li>
            ))}
          </ul>

          <div className="checkout-total">
            <p><strong>Total:</strong> ${total.toFixed(2)}</p>
            {!showPaymentForm && (
              <button className="add-to-cart-button" onClick={handleProceedToPayment}>
                Proceed to Payment
              </button>
            )}
          </div>
                    {showGuestCard && (
                      <div className="guest-cart-card">
                        <h3>Checkout Options</h3>

                        <button
                          className="signin-btn"
                          onClick={() => window.location.href = "/signin"}
                        >
                          Sign In
                        </button>

                        <p>or continue as guest</p>

                        <form onSubmit={handleGuestCheckout}>
                          <input
                            type="email"
                            value={guestEmail}
                            onChange={(e) => setGuestEmail(e.target.value)}
                            placeholder="Enter your email"
                            className="guest-email-input"
                            required
                          />

                          <button type="submit">
                            Continue to Payment
                          </button>
                        </form>
                      </div>
                    )}
       {showPaymentForm && (
            <Elements stripe={stripePromise}>
            <StripePay
  total={total}
  cartItems={cartItems}
  authUser={authUser}
  guestEmail={guestEmail || localStorage.getItem("guestEmail")}
  onPaymentSuccess={handlePaymentSuccess}
            />
            </Elements>
          )}
        </>
      )}
    </div>
  );
};

export default CheckOut;
